<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <!-- Header -->
    <header class="relative">
      <!-- Navigation -->
      <nav class="absolute top-0 left-0 right-0 z-50">
        <div class="container mx-auto px-6 py-4">
          <div class="flex items-center justify-between">
            <!-- Logo -->
            <router-link to="/" class="flex items-center space-x-3 group">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-12 h-12 rounded-2xl shadow-lg shadow-emerald-500/25" />
              <span class="text-2xl font-bold text-white">OilBuddyAI</span>
            </router-link>

            <!-- Navigation Links -->
            <div class="hidden md:flex items-center space-x-8">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-emerald-400 font-medium">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>

            <!-- Mobile Menu Button -->
            <button @click="toggleMobileMenu" class="md:hidden text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>

          <!-- Mobile Menu -->
          <div v-if="showMobileMenu" class="md:hidden mt-4 py-4 border-t border-slate-700">
            <div class="flex flex-col space-y-4">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-emerald-400 font-medium">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="pt-24 pb-16">
      <div class="container mx-auto px-6">
        <!-- Page Header -->
        <div class="text-center mb-16">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Support Center
          </div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            How Can We Help You?
          </h1>
          <p class="text-xl text-slate-300 max-w-2xl mx-auto">
            Find answers to common questions, get help with OilBuddyAI, and learn how to make the most of our platform.
          </p>
        </div>

        <!-- Quick Help Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div class="support-card group" @click="scrollToSection('faq')">
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">FAQ</h3>
            <p class="text-slate-400 text-sm">Find answers to common questions</p>
          </div>

          <div class="support-card group" @click="scrollToSection('guides')">
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition-colors">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">User Guides</h3>
            <p class="text-slate-400 text-sm">Step-by-step tutorials</p>
          </div>

          <div class="support-card group" @click="scrollToSection('contact')">
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Contact Us</h3>
            <p class="text-slate-400 text-sm">Get in touch with our team</p>
          </div>

          <div class="support-card group" @click="openVideoTutorials">
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Video Tutorials</h3>
            <p class="text-slate-400 text-sm">Watch helpful videos</p>
          </div>
        </div>

        <!-- FAQ Section -->
        <div id="faq" class="mb-16">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-3xl font-bold text-white">Frequently Asked Questions</h2>
            <div class="flex items-center space-x-4">
              <input 
                v-model="faqSearch" 
                type="text" 
                placeholder="Search FAQ..." 
                class="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              >
            </div>
          </div>

          <div class="space-y-4">
            <div 
              v-for="(category, categoryKey) in filteredFAQ" 
              :key="categoryKey" 
              class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8"
            >
              <h3 class="text-xl font-semibold text-white mb-6">{{ category.title }}</h3>
              <div class="space-y-4">
                <div 
                  v-for="(item, index) in category.items" 
                  :key="index"
                  class="border border-slate-700/50 rounded-lg overflow-hidden"
                >
                  <button 
                    @click="toggleFAQ(categoryKey, index)"
                    class="w-full px-6 py-4 text-left bg-slate-700/30 hover:bg-slate-700/50 transition-colors duration-200 flex items-center justify-between"
                  >
                    <span class="text-white font-medium">{{ item.question }}</span>
                    <svg 
                      class="w-5 h-5 text-slate-400 transition-transform duration-200"
                      :class="{ 'rotate-180': openFAQs.includes(`${categoryKey}-${index}`) }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  <div 
                    v-show="openFAQs.includes(`${categoryKey}-${index}`)"
                    class="px-6 py-4 bg-slate-700/20 border-t border-slate-700/50"
                  >
                    <p class="text-slate-300">{{ item.answer }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- User Guides Section -->
        <div id="guides" class="mb-16">
          <h2 class="text-3xl font-bold text-white mb-8">User Guides & Tutorials</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Getting Started</h3>
              <p class="text-slate-400 text-sm mb-4">Learn the basics of OilBuddyAI and set up your account</p>
              <button @click="openGuide('getting-started')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>

            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Job Management</h3>
              <p class="text-slate-400 text-sm mb-4">Master job tracking, scheduling, and management features</p>
              <button @click="openGuide('job-management')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>

            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Financial Tools</h3>
              <p class="text-slate-400 text-sm mb-4">Learn about invoicing, payments, and financial tracking</p>
              <button @click="openGuide('financial-tools')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>

            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">AI Features</h3>
              <p class="text-slate-400 text-sm mb-4">Discover AI-powered tools and automation features</p>
              <button @click="openGuide('ai-features')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>

            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Safety & Compliance</h3>
              <p class="text-slate-400 text-sm mb-4">Understand safety protocols and compliance requirements</p>
              <button @click="openGuide('safety-compliance')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>

            <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-colors duration-300">
              <div class="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Troubleshooting</h3>
              <p class="text-slate-400 text-sm mb-4">Common issues and how to resolve them</p>
              <button @click="openGuide('troubleshooting')" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Read Guide →
              </button>
            </div>
          </div>
        </div>

        <!-- Contact Section -->
        <div id="contact" class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
          <h2 class="text-3xl font-bold text-white mb-6">Still Need Help?</h2>
          <p class="text-slate-300 mb-8 max-w-2xl">
            Can't find what you're looking for? Our support team is here to help you get the most out of OilBuddyAI.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="text-center">
              <div class="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Email Support</h3>
              <p class="text-slate-400 text-sm mb-4">Get help via email within 24 hours</p>
              <a href="mailto:support@oilbuddy.ai" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                support@oilbuddy.ai
              </a>
            </div>

            <div class="text-center">
              <div class="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Live Chat</h3>
              <p class="text-slate-400 text-sm mb-4">Chat with our support team</p>
              <button @click="startLiveChat" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                Start Chat
              </button>
            </div>

            <div class="text-center">
              <div class="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-white mb-2">Phone Support</h3>
              <p class="text-slate-400 text-sm mb-4">Call us during business hours</p>
              <a href="tel:+1-800-OILBUDDY" class="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 text-sm font-medium">
                +1 (800) OILBUDDY
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-slate-900/50 border-t border-slate-700/50">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-3 mb-4">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-10 h-10 rounded-xl shadow-lg shadow-emerald-500/25" />
              <span class="text-xl font-bold text-white">OilBuddyAI</span>
            </div>
            <p class="text-slate-400 mb-6 max-w-md">
              Revolutionizing the oil and gas industry with AI-powered solutions for contractors, companies, and professionals.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul class="space-y-2">
              <li><router-link to="/" class="text-slate-400 hover:text-white transition-colors duration-200">Home</router-link></li>
              <li><router-link to="/blog" class="text-slate-400 hover:text-white transition-colors duration-200">Blog</router-link></li>
              <li><router-link to="/support" class="text-slate-400 hover:text-white transition-colors duration-200">Support</router-link></li>
              <li><router-link to="/contact" class="text-slate-400 hover:text-white transition-colors duration-200">Contact</router-link></li>
              <li><router-link to="/roadmap" class="text-slate-400 hover:text-white transition-colors duration-200">Roadmap</router-link></li>
            </ul>
          </div>

          <!-- Contact Info -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Contact</h3>
            <ul class="space-y-2">
              <li><a href="mailto:support@oilbuddy.ai" class="text-slate-400 hover:text-white transition-colors duration-200">support@oilbuddy.ai</a></li>
              <li><a href="tel:+1-800-OILBUDDY" class="text-slate-400 hover:text-white transition-colors duration-200">+1 (800) OILBUDDY</a></li>
              <li class="text-slate-400">Houston, TX</li>
            </ul>
          </div>
        </div>

        <div class="border-t border-slate-700/50 mt-8 pt-8 text-center">
          <p class="text-slate-400">&copy; 2024 OilBuddyAI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

// Mobile menu state
const showMobileMenu = ref(false)

// FAQ state
const openFAQs = ref<string[]>([])
const faqSearch = ref('')

// FAQ data
const faqs = {
  general: {
    title: 'General Questions',
    items: [
      {
        question: 'What is OilBuddyAI?',
        answer: 'OilBuddyAI is a comprehensive AI-powered platform designed specifically for oilfield contractors and companies. It helps streamline operations, manage jobs, handle finances, ensure safety compliance, and optimize business processes using advanced artificial intelligence.'
      },
      {
        question: 'How much does OilBuddyAI cost?',
        answer: 'We offer flexible pricing plans starting from $29/month for contractors and $99/month for companies. We also have enterprise plans for larger organizations. All plans include a 14-day free trial with no credit card required.'
      },
      {
        question: 'Is my data secure?',
        answer: 'Yes, we use enterprise-grade security measures including encryption, secure data centers, and compliance with industry standards. Your data is protected with the same level of security used by major financial institutions.'
      },
      {
        question: 'Can I cancel my subscription anytime?',
        answer: 'Absolutely! You can cancel your subscription at any time with no penalties or hidden fees. Your data will be available for 30 days after cancellation, and you can reactivate your account anytime.'
      }
    ]
  },
  features: {
    title: 'Features & Functionality',
    items: [
      {
        question: 'What AI features are included?',
        answer: 'Our AI features include intelligent job matching, automated invoicing, predictive analytics for project timelines, safety incident prediction, financial optimization recommendations, and smart document processing.'
      },
      {
        question: 'Can I integrate with my existing systems?',
        answer: 'Yes, we offer API integrations and can work with your existing accounting, payroll, project management, and CRM systems. We support popular platforms like QuickBooks, Salesforce, and more.'
      },
      {
        question: 'Do you offer mobile apps?',
        answer: 'Yes, we have mobile apps available for both iOS and Android devices, allowing you to manage your operations on the go with full functionality.'
      },
      {
        question: 'How does the job marketplace work?',
        answer: 'Our job marketplace connects contractors with companies looking for services. AI algorithms match the best contractors to jobs based on skills, location, availability, and past performance.'
      }
    ]
  },
  technical: {
    title: 'Technical Support',
    items: [
      {
        question: 'What browsers are supported?',
        answer: 'We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of Chrome.'
      },
      {
        question: 'How do I reset my password?',
        answer: 'You can reset your password by clicking the "Forgot Password" link on the login page. You\'ll receive an email with instructions to create a new password.'
      },
      {
        question: 'Can I export my data?',
        answer: 'Yes, you can export your data in various formats including CSV, PDF, and Excel. This includes job data, financial reports, and other important information.'
      },
      {
        question: 'Is there a limit on file uploads?',
        answer: 'File upload limits depend on your plan. Free plans have a 10MB limit per file, while paid plans support up to 100MB per file with unlimited storage.'
      }
    ]
  }
}

// Computed properties
const filteredFAQ = computed(() => {
  if (!faqSearch.value) return faqs
  
  const filtered: any = {}
  Object.keys(faqs).forEach(categoryKey => {
    const category = faqs[categoryKey as keyof typeof faqs]
    const filteredItems = category.items.filter(item =>
      item.question.toLowerCase().includes(faqSearch.value.toLowerCase()) ||
      item.answer.toLowerCase().includes(faqSearch.value.toLowerCase())
    )
    if (filteredItems.length > 0) {
      filtered[categoryKey] = {
        title: category.title,
        items: filteredItems
      }
    }
  })
  return filtered
})

// Methods
const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const toggleFAQ = (categoryKey: string, index: number) => {
  const faqKey = `${categoryKey}-${index}`
  const faqIndex = openFAQs.value.indexOf(faqKey)
  if (faqIndex > -1) {
    openFAQs.value.splice(faqIndex, 1)
  } else {
    openFAQs.value.push(faqKey)
  }
}

const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const openGuide = (guideType: string) => {
  alert(`Opening guide: ${guideType}. This feature will be available soon!`)
}

const openVideoTutorials = () => {
  alert('Video tutorials will be available soon! Check back later for helpful video guides.')
}

const startLiveChat = () => {
  alert('Live chat feature coming soon! For now, please use our contact form or email us directly.')
}
</script>

<style scoped>
.support-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer;
}

.btn-outline {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors duration-200;
}

.btn-primary {
  @apply inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200;
}
</style>
